import React, { useLayoutEffect, useMemo, useRef } from "react";
import { useScreenConfig } from "../hooks/useScreenConfig";
import { ComponentRenderer } from "./ComponentRenderer";
import { useAppDispatch, useAppSelector } from "../state/hooks";
import { useDarkMode } from "../contexts/DarkModeContext";
import { selectForms, selectUser } from "../state/selectors";
import {
    updateScreenFormField,
    setScreenFormData,
    setScreenLoading,
} from "../state/slices/formsSlice";
import { showNotification } from "../state/slices/notificationsSlice";
import { apiService } from "../services/api";
import { buildFormDataFromRecord } from "../state/utils/configUtils";
import { parseHrmsEmployeeProfileGetResponse } from "../utils/parseHrmsEmployeeProfileResponse";
import loaderLight from "../../public/images/loader_light.gif";
import loaderDark from "../../public/images/loader_dark.gif";
import { usePermissions } from "../hooks/usePermissions";
import type { ComponentConfig } from "../services/types/ComponentTypes";
import { getUserRole } from "../utils/visibilityUtils";

const EMPLOYEE_PROFILE_SCREEN_ID = "2217";

interface ScreenRendererProps {
    screenId: string;
    onActionTrigger: (actionId: string, data?: any) => void;
    refreshKey?: number;
    renderMode?: "page" | "popup";
}

export const ScreenRenderer: React.FC<ScreenRendererProps> = ({
    screenId,
    onActionTrigger,
    refreshKey,
    renderMode = "page",
}) => {
    const { config, loading: configLoading, error, source } = useScreenConfig(screenId);
    const dispatch = useAppDispatch();
    const formsState = useAppSelector(selectForms);
    const user = useAppSelector(selectUser);
    const { isDarkMode } = useDarkMode();
    const { hasPermission } = usePermissions();
    const profileLoadGenRef = useRef(0);

    // Get form data and loading state from formsState
    // Note: 'loadingByScreen' in formsState tracks DATA loading (e.g. initial values), 
    // while 'configLoading' tracks CONFIGURATION loading.
    const isDataLoading = formsState.loadingByScreen[screenId] || false;
    const formData = formsState.formDataByScreen[screenId] || {};

    // Combine loading states
    const isLoading = configLoading || isDataLoading;

    /**
     * Recursively removes components from a config array that the user lacks
     * permission for. A component is removed when it has a `permissionKey` and
     * that permission evaluates to false for the given `pScreenId`.
     *
     * NOTE: must be defined before any early returns to satisfy Rules of Hooks.
     */
    const filterByPermission = useMemo(() => {
        return function filter(components: ComponentConfig[], pScreenId: string | undefined): ComponentConfig[] {
            return components.reduce<ComponentConfig[]>((acc, comp) => {
                const c = comp as any;

                // Check permission gate on the component itself
                if (c.permissionKey && pScreenId) {
                    if (!hasPermission(pScreenId, c.permissionKey)) {
                        return acc; // drop this component
                    }
                }

                // Determine child screenId: if this component declares its own screenId, use it;
                // otherwise inherit the parent's
                const childScreenId: string | undefined = c.screenId ?? pScreenId;

                // Filter SummeryActions (row-action buttons on SummaryList)
                let filteredSummeryActions = c.SummeryActions;
                if (Array.isArray(c.SummeryActions) && childScreenId) {
                    filteredSummeryActions = c.SummeryActions.filter(
                        (action: any) =>
                            !action.permissionKey ||
                            hasPermission(childScreenId, action.permissionKey)
                    );
                }

                // Filter fabAction
                let filteredFabAction = c.fabAction;
                if (c.fabAction && c.fabAction.permissionKey && childScreenId) {
                    if (!hasPermission(childScreenId, c.fabAction.permissionKey)) {
                        filteredFabAction = undefined;
                    }
                }

                // Recurse into childComponents
                if (c.childComponents && Array.isArray(c.childComponents)) {
                    const filtered = filter(c.childComponents, childScreenId);
                    const newC = { ...c, childComponents: filtered };
                    if (filteredSummeryActions !== c.SummeryActions) newC.SummeryActions = filteredSummeryActions;
                    if (filteredFabAction === undefined) delete newC.fabAction;
                    else if (filteredFabAction !== c.fabAction) newC.fabAction = filteredFabAction;
                    acc.push(newC);
                } else {
                    const newC = { ...c };
                    if (filteredSummeryActions !== c.SummeryActions) newC.SummeryActions = filteredSummeryActions;
                    if (filteredFabAction === undefined) delete newC.fabAction;
                    else if (filteredFabAction !== c.fabAction) newC.fabAction = filteredFabAction;

                    if (filteredSummeryActions !== c.SummeryActions || filteredFabAction !== c.fabAction) {
                        acc.push(newC);
                    } else {
                        acc.push(comp);
                    }
                }

                return acc;
            }, []);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasPermission]);

    // Resolve the screenId declared in config (root ScreenGroupComponent may have screenId)
    // Use config ?? [] so this memo runs safely even before config is loaded.
    const configScreenId: string | undefined = (config?.[0] as any)?.screenId;

    // Apply permission-based filtering — MUST be before any early returns (Rules of Hooks)
    const filteredConfig = useMemo(
        () => filterByPermission(config ?? [], configScreenId),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [config, configScreenId, filterByPermission]
    );

    // Screen-specific cleanup for config loaded from DB/API.
    // Attendance summary (screen 2200): remove the top date filter control.
    const screenSafeConfig = useMemo(() => {
        if (screenId !== "2200") return filteredConfig;

        const roleId = getUserRole();

        const stripDateFilter = (components: ComponentConfig[]): ComponentConfig[] => {
            return components.reduce<ComponentConfig[]>((acc, comp) => {
                const c: any = comp as any;
                const compType = String(c.componentType ?? "").trim().toLowerCase();
                const customType = String(c.customType ?? "").trim().toLowerCase();

                const isDateFilter =
                    compType === "custom" && customType === "datefilter";

                if (isDateFilter) return acc;

                // Attendance (admin / non-employee): remove the CheckInOut section entirely.
                // Some configs hide only the inner CHECK_IN_OUT component via `visibility: [2]`,
                // leaving an empty GRID/GROUP wrapper that still consumes space.
                if (roleId !== 2) {
                    const containsCheckInOutOnly = (node: any): boolean => {
                        if (!node) return false;
                        const t = String(node.componentType ?? "").toLowerCase();
                        // leaf
                        if (t === "check_in_out_component" || t === "checkinout" || t === "checkinoutcomponent") return true;
                        const kids: any[] = Array.isArray(node.childComponents) ? node.childComponents : [];
                        if (kids.length === 0) return false;
                        // true only if *all* descendants are wrappers around CheckInOut
                        return kids.every((k) => containsCheckInOutOnly(k) || (Array.isArray(k?.childComponents) && containsCheckInOutOnly(k)));
                    };

                    // If this node is a layout wrapper whose subtree is only CheckInOut, drop it.
                    if (containsCheckInOutOnly(c)) {
                        return acc;
                    }
                }

                if (Array.isArray(c.childComponents)) {
                    const nextChildren = stripDateFilter(c.childComponents);
                    if (nextChildren !== c.childComponents) {
                        acc.push({ ...(c as any), childComponents: nextChildren } as any);
                    } else {
                        acc.push(comp);
                    }
                    return acc;
                }

                acc.push(comp);
                return acc;
            }, []);
        };

        return stripDateFilter(filteredConfig ?? []);
    }, [filteredConfig, screenId]);

    // In popup mode, strip "Back" buttons so the modal "X" is the only close affordance.
    // Also strip popup-only controls that aren't useful in constrained modal contexts.
    const popupSafeConfig = useMemo(() => {
        if (renderMode !== "popup") return screenSafeConfig;

        const stripPopupOnlyControls = (components: ComponentConfig[]): ComponentConfig[] => {
            return components.reduce<ComponentConfig[]>((acc, comp) => {
                const c: any = comp as any;
                const compType = c.componentType;
                const name = String(c.name ?? c.Label ?? "").trim().toLowerCase();
                const onPress = String(c.onPressAction ?? "").trim().toLowerCase();

                const shouldStrip =
                    String(compType).toLowerCase() === "button" &&
                    name === "back" &&
                    (onPress.startsWith("navigate_") || onPress === "navigate_back" || onPress === "cancel");

                // Keep all date filters in popup; we default them to today via formData.
                if (shouldStrip) return acc;

                if (Array.isArray(c.childComponents)) {
                    const nextChildren = stripPopupOnlyControls(c.childComponents);
                    if (nextChildren !== c.childComponents) {
                        acc.push({ ...(c as any), childComponents: nextChildren } as any);
                    } else {
                        acc.push(comp);
                    }
                    return acc;
                }

                acc.push(comp);
                return acc;
            }, []);
        };

        return stripPopupOnlyControls(screenSafeConfig ?? []);
    }, [screenSafeConfig, renderMode, screenId]);

    useLayoutEffect(() => {
        if (screenId !== EMPLOYEE_PROFILE_SCREEN_ID) return;
        if (configLoading || !config?.length) return;

        const employeeIdRaw = user?.employee_id;
        const employeeId =
            employeeIdRaw !== undefined && employeeIdRaw !== null
                ? String(employeeIdRaw).trim()
                : "";

        const gen = ++profileLoadGenRef.current;
        const ac = new AbortController();

        dispatch(
            setScreenLoading({
                screenId: EMPLOYEE_PROFILE_SCREEN_ID,
                isLoading: true,
            })
        );

        (async () => {
            try {
                const endpoint = employeeId
                    ? `/pabws/hrms/employee/profile/${encodeURIComponent(employeeId)}`
                    : `/pabws/hrms/employee/profile/me`;
                const raw = await apiService.get<any>(
                    endpoint,
                    undefined,
                    {
                        includeAuthToken: true,
                        authHeaderType: "Bearer",
                        errorHandling: "return-null",
                        signal: ac.signal,
                    }
                );

                if (ac.signal.aborted) {
                    return;
                }
                if (gen !== profileLoadGenRef.current) {
                    return;
                }

                const record = parseHrmsEmployeeProfileGetResponse(raw) as
                    | Record<string, any>
                    | null;

                if (!record) {
                    dispatch(
                        showNotification({
                            message: "Could not load employee profile.",
                            type: "error",
                        })
                    );
                    dispatch(
                        setScreenFormData({
                            screenId: EMPLOYEE_PROFILE_SCREEN_ID,
                            data: {},
                        })
                    );
                    return;
                }

                const mapped = buildFormDataFromRecord(record, {
                    screenId: EMPLOYEE_PROFILE_SCREEN_ID,
                });
                const rowId = record.id ?? record.employee_id ?? employeeId;
                dispatch(
                    setScreenFormData({
                        screenId: EMPLOYEE_PROFILE_SCREEN_ID,
                        data: {
                            ...mapped,
                            id: rowId,
                            employee_id: String(rowId),
                        },
                    })
                );
            } catch {
                if (!ac.signal.aborted && gen === profileLoadGenRef.current) {
                    dispatch(
                        showNotification({
                            message: "Could not load employee profile.",
                            type: "error",
                        })
                    );
                    dispatch(
                        setScreenFormData({
                            screenId: EMPLOYEE_PROFILE_SCREEN_ID,
                            data: {},
                        })
                    );
                }
            } finally {
                if (gen === profileLoadGenRef.current) {
                    dispatch(
                        setScreenLoading({
                            screenId: EMPLOYEE_PROFILE_SCREEN_ID,
                            isLoading: false,
                        })
                    );
                }
            }
        })();

        return () => {
            ac.abort();
            dispatch(
                setScreenLoading({
                    screenId: EMPLOYEE_PROFILE_SCREEN_ID,
                    isLoading: false,
                })
            );
        };
    }, [
        screenId,
        configLoading,
        config,
        user?.employee_id,
        refreshKey,
        dispatch,
    ]);

    if (isLoading) {
        return (
            <div className="flex justify-center items-center min-h-[50vh] flex-col gap-4">
                <div className="w-10 h-10" />
                <img
                    src={isDarkMode ? loaderDark : loaderLight}
                    alt="Loading"
                    className="w-00 h-80"
                />
                {configLoading && <p className="text-gray-500 text-sm">Loading configuration...</p>}
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-6 text-center text-danger-600 dark:text-danger-400">
                {error}
            </div>
        );
    }

    if (!config) {
        return (
            <div className="p-6 text-center text-danger-600 dark:text-danger-400">
                Screen configuration not found for ID: {screenId}
            </div>
        );
    }

    // Dev indicator for config source
    const showDevIndicator = import.meta.env.DEV && source;


    return (
        <div className="relative">
            {showDevIndicator && (
                <div
                    className={`absolute top-0 right-0 z-50 px-2 py-1 text-xs font-bold rounded-bl-md shadow-sm pointer-events-none opacity-90 border-l border-b ${
                        source === 'API'
                            ? 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/90 dark:text-blue-200 dark:border-blue-700'
                            : 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-amber-900/90 dark:text-amber-200 dark:border-amber-700'
                    }`}
                    title={`Configuration loaded from ${source}`}
                >
                    Config: {source}
                </div>
            )}

            <ComponentRenderer
                config={popupSafeConfig}
                formData={formData}
                onFormDataChange={(name, value) =>
                    dispatch(updateScreenFormField({ screenId, name, value }))
                }
                onActionTrigger={onActionTrigger}
                refreshKey={refreshKey}
                renderMode={renderMode}
            />
        </div>
    );
};
